import { PopoverList, Tag } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { FeatureRole, Guide, Resource, ResourceType, Seller } from "types";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useGuides } from "@/hooks/useSellerResource";
import { useModal } from "@/hooks/useVisibility";
import { hasFeature } from "@/models/Seller";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardGroupBy } from "@/types/Dashboard";
import { getSeller } from "@/utils/getSeller";

const items = [
    {
        name: "None",
        key: DashboardGroupBy.NONE,
    },
    {
        name: "Arrival Time",
        key: DashboardGroupBy.ARRIVAL_TIME,
    },
    {
        name: "Equipment",
        key: DashboardGroupBy.EQUIPMENT,
    },
    {
        name: "Event",
        key: DashboardGroupBy.EVENT,
    },
    {
        name: "Guide",
        key: DashboardGroupBy.GUIDE,
    },
    {
        name: "Pick-up Location",
        key: DashboardGroupBy.PICKUP,
    },
    {
        name: "Product",
        key: DashboardGroupBy.PRODUCT,
    },
];
const getAvailableGroupByItems = (seller: Seller, guides: Guide[], equipments: Resource[]) => {
    return items.filter((item) => {
        if (item.key === DashboardGroupBy.GUIDE) {
            return hasFeature(seller, FeatureRole.GUIDE_MANAGER) && guides.length > 0;
        }
        if (item.key === DashboardGroupBy.EQUIPMENT) {
            return (
                hasFeature(seller, FeatureRole.EQUIPMENT_MANAGER) &&
                hasFeature(seller, FeatureRole.SELLER_INVENTORY) &&
                equipments.length > 0
            );
        }
        return true;
    });
};

const handleFilterChange = (groupBy: DashboardGroupBy) => {
    setTimeout(() => {
        useDashboardFilterStore.getState().setFilters({ groupBy });
    }, 0);
};

export const DashboardGroupByFilter = () => {
    const popover = useModal();
    const seller = getSeller();
    const { guides } = useGuides(null, { enabled: true });
    const { data: resources } = useInfiniteCollection<Resource>("resources");
    const equipments = resources.filter((resource) => resource.type === ResourceType.EQUIPMENT);
    const groupBy = useDashboardFilterStore.getState().filters.groupBy;
    const availableItems = getAvailableGroupByItems(seller, guides, equipments);

    return (
        <PopoverList placement="top" appendTo={document.body} visible={popover.isVisible} onClickOutside={popover.hide}>
            <FilterTag
                value={items.find((item) => item.key === groupBy)?.name ?? "None"}
                label="Group By"
                onClick={popover.toggle}
            />

            {availableItems.map((item) => {
                return (
                    <PopoverList.Item
                        key={item.key}
                        name={item.name}
                        isActive={item.key === groupBy}
                        onClickItem={() => {
                            popover.hide();
                            handleFilterChange(item.key);
                        }}
                    >
                        {item.name}
                    </PopoverList.Item>
                );
            })}
        </PopoverList>
    );
};
interface FilterTagProps {
    value: string;
    onClick: () => void;
    label: string;
    classNames?: string;
}

const FilterTag = ({ label, value, onClick, classNames }: FilterTagProps) => {
    return (
        <Tag
            color="primary"
            size="medium"
            className={clsx("leading-5 hover:cursor-pointer hover:border-black hover:bg-gray-light")}
            onClick={onClick}
        >
            <span className="inline-flex items-center">
                <span className="mr-1 font-bold">{label}:</span>
                <span className={clsx("text-justify", classNames ?? "min-w-12")}>{value}</span>
            </span>
        </Tag>
    );
};
